import { assertNever } from "@redotech/util/type";
import * as z from "zod";
import { HsaQuestionType } from "./hsa-question-type";

// Answer kinds — collapsed view of the question type union focused on
// storage shape. Multiple question types can share one answer kind:
//
//   AGE_INPUT_QUESTION                → "age"        (number)
//   EMAIL_INPUT_QUESTION              → "text"       (string)
//   TEXT_INPUT_QUESTION               → "text"       (string)
//   SINGLE_SELECT_RADIO_QUESTION      → "single_select"
//   SINGLE_SELECT_DROPDOWN_QUESTION   → "single_select"
//   SINGLE_SELECT_OTHER_QUESTION      → "single_select_other"
//   MULTI_SELECT_QUESTION             → "multi_select"
//   MULTI_SELECT_NONE_QUESTION        → "multi_select"
//
// Discriminator field is `kind` (not `type`) so there's no conflation with
// the question type's `type` discriminator. Every answer carries the
// `questionHandle` so it can be paired with a pool question and stored under
// the (session_id, question_slug) unique constraint.

export enum HsaAnswerKind {
  TEXT = "text",
  AGE = "age",
  SINGLE_SELECT = "single_select",
  SINGLE_SELECT_OTHER = "single_select_other",
  MULTI_SELECT = "multi_select",
}

const hsaAnswerBase_ = z.object({ questionHandle: z.string().min(1) });

const hsaTextAnswer_ = hsaAnswerBase_.extend({
  kind: z.literal(HsaAnswerKind.TEXT),
  text: z.string(),
});
export const hsaTextAnswer: z.ZodType<HsaTextAnswer> = hsaTextAnswer_;
export interface HsaTextAnswer extends z.infer<typeof hsaTextAnswer_> {}

const hsaAgeAnswer_ = hsaAnswerBase_.extend({
  kind: z.literal(HsaAnswerKind.AGE),
  value: z.number().int(),
});
export const hsaAgeAnswer: z.ZodType<HsaAgeAnswer> = hsaAgeAnswer_;
export interface HsaAgeAnswer extends z.infer<typeof hsaAgeAnswer_> {}

const hsaSingleSelectAnswer_ = hsaAnswerBase_.extend({
  kind: z.literal(HsaAnswerKind.SINGLE_SELECT),
  optionValue: z.string().min(1),
});
export const hsaSingleSelectAnswer: z.ZodType<HsaSingleSelectAnswer> =
  hsaSingleSelectAnswer_;
export interface HsaSingleSelectAnswer extends z.infer<
  typeof hsaSingleSelectAnswer_
> {}

// SINGLE_SELECT_OTHER answers always carry an optionValue. When the
// customer picked the "other" option, optionValue === SINGLE_SELECT_OTHER_OPTION_VALUE
// AND otherText holds the free-text. When they picked any other option,
// optionValue is that option's value and otherText is omitted.
const hsaSingleSelectOtherAnswer_ = hsaAnswerBase_.extend({
  kind: z.literal(HsaAnswerKind.SINGLE_SELECT_OTHER),
  optionValue: z.string().min(1),
  otherText: z.string().optional(),
});
export const hsaSingleSelectOtherAnswer: z.ZodType<HsaSingleSelectOtherAnswer> =
  hsaSingleSelectOtherAnswer_;
export interface HsaSingleSelectOtherAnswer extends z.infer<
  typeof hsaSingleSelectOtherAnswer_
> {}

const hsaMultiSelectAnswer_ = hsaAnswerBase_.extend({
  kind: z.literal(HsaAnswerKind.MULTI_SELECT),
  optionValues: z.array(z.string().min(1)),
});
export const hsaMultiSelectAnswer: z.ZodType<HsaMultiSelectAnswer> =
  hsaMultiSelectAnswer_;
export interface HsaMultiSelectAnswer extends z.infer<
  typeof hsaMultiSelectAnswer_
> {}

const hsaAnswer_ = z.discriminatedUnion("kind", [
  hsaTextAnswer_,
  hsaAgeAnswer_,
  hsaSingleSelectAnswer_,
  hsaSingleSelectOtherAnswer_,
  hsaMultiSelectAnswer_,
]);
export const hsaAnswer: z.ZodType<HsaAnswer> = hsaAnswer_;
export type HsaAnswer = z.infer<typeof hsaAnswer_>;

// Many-to-one map from question type to answer kind. Source of truth for
// "what answer shape does this question expect" — used by answer validators
// and the customer-portal RPC handler.
export function hsaAnswerKindForQuestionType(
  type: HsaQuestionType,
): HsaAnswerKind {
  switch (type) {
    case HsaQuestionType.AGE_INPUT_QUESTION:
      return HsaAnswerKind.AGE;
    case HsaQuestionType.EMAIL_INPUT_QUESTION:
    case HsaQuestionType.TEXT_INPUT_QUESTION:
    case HsaQuestionType.DATE_INPUT_QUESTION:
    case HsaQuestionType.PHONE_INPUT_QUESTION:
      return HsaAnswerKind.TEXT;
    case HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION:
    case HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION:
      return HsaAnswerKind.SINGLE_SELECT;
    case HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION:
      return HsaAnswerKind.SINGLE_SELECT_OTHER;
    case HsaQuestionType.MULTI_SELECT_QUESTION:
    case HsaQuestionType.MULTI_SELECT_NONE_QUESTION:
    case HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION:
      return HsaAnswerKind.MULTI_SELECT;
    default:
      assertNever(type);
  }
}
